import React, { useState, useEffect, useRef } from "react"
import { motion } from "framer-motion"
import { navigate, graphql } from "gatsby"

import { Box, Flex, SEO } from "components"

import TitleCard from "./views/titleCard"
import PreviewCard from "./views/previewCard"
import Gallery from "./views/gallery"

const topSpace = "80px"
const bottomSpace = "32px"

const ProjectTemplate = ({ isMobile, location, preview, data }) => {
  const galleryRef = useRef(null)
  const scrollRef = useRef(null)
  const nextRef = useRef(null)
  const [fixed, setFixed] = useState(false)
  const [opacity, setOpacity] = useState(1)
  const [project, setProject] = useState({})
  const [nextProject, setNextProject] = useState(undefined)

  const project_data = JSON.parse(data.project.dataString)

  const hijackScroll = e => {
    if (scrollRef && scrollRef.current) {
      scrollRef.current.scrollLeft += e.deltaY
    }
    if (
      nextRef &&
      nextRef.current &&
      data &&
      data.nextProject &&
      data.nextProject.uid
    ) {
      let left = nextRef.current.getBoundingClientRect().left
      if (left <= 0) {
        navigate("/project/" + data.nextProject.uid + "/?scroll=" + left * -1)
      }
    }
    if (
      galleryRef &&
      galleryRef.current &&
      data &&
      data.nextProject &&
      data.nextProject.uid
    ) {
      const width =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth
      let rect = galleryRef.current.getBoundingClientRect()
      let left = rect.left
      let right = rect.right
      if (left <= 80) {
        setOpacity((right - width / 3) / (width / 3))
        setFixed(true)
      } else {
        setFixed(false)
      }
    }
  }

  useEffect(() => {
    if (location.search && location.search.indexOf("?scroll=") !== -1) {
      if (scrollRef && scrollRef.current) {
        scrollRef.current.scrollLeft = parseFloat(
          location.search.replace("?scroll=", "")
        )
      }
    }
    if (location.search && location.search.indexOf("?topScroll=") !== -1) {
      let top = parseFloat(location.search.replace("?topScroll=", ""))
      document.documentElement.scrollTop = top
    }
    if (preview || (data.project && data.project.dataString)) {
      let dataParsed = preview
        ? preview.data
        : JSON.parse(data.project.dataString)
      setProject({
        title:
          dataParsed.client_name &&
          dataParsed.client_name[0] &&
          dataParsed.client_name[0].text,
        season: dataParsed.season,
        agencyCredit: dataParsed.agency_credit,
        studioPartnerCredit: dataParsed.studio_partner_credit,
        creativeDirectorCredit: dataParsed.creative_director_credit,
        directorCredit: dataParsed.director_credit,
        videoCredit: dataParsed.video_credit,
        photoCredit: dataParsed.photo_credit,
        featuredVideo: {
          url: dataParsed.featured_video && dataParsed.featured_video.url,
          height: dataParsed.featured_video_height,
          width: dataParsed.featured_video_width,
        },
        featuredVimeo: {
          url: dataParsed.featured_vimeo && dataParsed.featured_vimeo.url,
        },
        category:
          dataParsed.category && dataParsed.category.name
            ? dataParsed.category.name[0].text
            : dataParsed.category.data.name &&
              dataParsed.category.data.name[0] &&
              dataParsed.category.data.name[0].text,
        gallery: dataParsed.gallery,
      })
    }
    if (data && data.nextProject && data.nextProject.dataString) {
      let dataParsed = JSON.parse(data.nextProject.dataString)
      setNextProject({
        uid: data.nextProject.uid,
        title:
          dataParsed.client_name &&
          dataParsed.client_name[0] &&
          dataParsed.client_name[0].text,
        featuredVideo: {
          url: dataParsed.featured_video && dataParsed.featured_video.url,
          height: dataParsed.featured_video_height,
          width: dataParsed.featured_video_width,
        },
        season: dataParsed.season,
        agencyCredit: dataParsed.agency_credit,
        studioPartnerCredit: dataParsed.studio_partner_credit,
        creativeDirectorCredit: dataParsed.creative_director_credit,
        directorCredit: dataParsed.director_credit,
        videoCredit: dataParsed.video_credit,
        photoCredit: dataParsed.photo_credit,
        category:
          dataParsed.category &&
          dataParsed.category.data.name &&
          dataParsed.category.data.name[0] &&
          dataParsed.category.data.name[0].text,
        gallery: dataParsed.gallery,
      })
    }
    if (!isMobile) {
      window.addEventListener("wheel", hijackScroll)
    }
    return function cleanup() {
      if (!isMobile) {
        window.removeEventListener("wheel", hijackScroll)
      }
    }
  }, [data, preview, location.search])

  return (
    <>
      <SEO
        title={
          project_data.client_name &&
          project_data.client_name[0] &&
          project_data.client_name[0].text +
            (project_data.category
              ? ": " +
                (project_data.category &&
                  project_data.category.data.name &&
                  project_data.category.data.name[0] &&
                  project_data.category.data.name[0].text)
              : "")
        }
        description={project_data.season}
        image={
          project_data.gallery &&
          project_data.gallery[0] &&
          project_data.gallery[0].photo &&
          project_data.gallery[0].photo.url
        }
      />

      <motion.div
        variants={{
          visible: {
            opacity: 1,
            transition: {
              delay:
                location.search.indexOf("?scroll=") !== -1 ||
                location.search.indexOf("?topScroll=") !== -1
                  ? 0
                  : 0.5,
              duration:
                location.search.indexOf("?scroll=") !== -1 ||
                location.search.indexOf("?topScroll=") !== -1
                  ? 0
                  : 0.6,
              ease: [0.215, 0.61, 0.355, 1],
            },
          },
          initial: {
            opacity: 0,
            transition: {
              when: "afterChildren",
            },
          },
        }}
        initial="initial"
        animate="visible"
        exit="exit"
        key={"xButton"}
      >
        <Flex
          as="button"
          position="fixed"
          top={0}
          left={0}
          justifyContent="center"
          alignItems="center"
          width={[48, topSpace]}
          height={[48, topSpace]}
          m={0}
          p={0}
          border={0}
          zIndex={10}
          bg="background"
          css={{ cursor: "pointer" }}
          onClick={() => {
            navigate("/")
          }}
        >
          <Box as="svg" viewBox="0 0 12 12" width={12} height={12} color="text">
            <path
              d="M-.354 12.646l12-12M.354.646l12 12"
              stroke="currentColor"
            />
          </Box>
        </Flex>
      </motion.div>

      <Box
        data-component-id="scrollable"
        display="flex"
        flexDirection={["column", null, "row"]}
        flexWrap="nowrap"
        height={["auto", null, "100vh"]}
        pt={[0, null, topSpace]}
        pb={[0, null, bottomSpace]}
        overflowX="auto"
        css={{ WebkitOverflowScrolling: "touch" }}
        ref={scrollRef}
      >
        {project && (
          <>
            <TitleCard location={location} project={project} />
            <Flex ref={galleryRef}>
              <Gallery
                topSpace={topSpace}
                bottomSpace={bottomSpace}
                isMobile={isMobile}
                location={location}
                project={project}
                position={isMobile ? "fixed" : fixed ? "fixed" : "absolute"}
                opacity={opacity}
              />
            </Flex>
          </>
        )}
        {!isMobile && nextProject && (
          <>
            <Flex ref={nextRef} flexDirection={["column", null, "row"]}>
              <TitleCard
                location={location}
                project={nextProject}
                next={true}
              />
              <Gallery
                location={location}
                isMobile={isMobile}
                project={nextProject}
                position={"absolute"}
                opacity={1}
                hideHeader={isMobile ? true : false}
                next={true}
                topSpace={topSpace}
                bottomSpace={bottomSpace}
              />
            </Flex>
          </>
        )}
        {isMobile && nextProject && (
          <>
            <Flex ref={nextRef} flexDirection={["column", null, "row"]}>
              <PreviewCard
                topSpace={topSpace}
                bottomSpace={bottomSpace}
                location={location}
                project={nextProject}
              />
            </Flex>
          </>
        )}
      </Box>
    </>
  )
}

export const query = graphql`
  query projectQuery($prismicId: String!, $nextProject: String!) {
    project: prismicProject(prismicId: { eq: $prismicId }) {
      dataString
      uid
    }
    nextProject: prismicProject(prismicId: { eq: $nextProject }) {
      dataString
      uid
    }
  }
`

export default ProjectTemplate
